import type { Poi } from "@/libs/models";
import type { FlightWithPOI, SitumFlightInformation } from "./types";

/**
 * Associates each flight with its gate/boarding-area POI ID.
 */
export const buildFlightPayloads = (
  flights: SitumFlightInformation[],
  pois: Poi[],
): FlightWithPOI[] => {
  const poisIndexedSet = indexPoisByKey(pois);
  return flights.map((flight) => ({
    flight,
    poiId: findAssociatedPoiId(flight, poisIndexedSet),
  }));
};

/**
 * Creates an indexed map of POIs by their gate and/or
 * boarding area value.
 */
const indexPoisByKey = (pois: Poi[]): Map<string, Poi> => {
  const map = new Map<string, Poi>();

  for (const poi of pois) {
    const name = poi.name;
    if (name.startsWith("Gate - ")) {
      const gate = name.split(" - ")[1];
      if (gate) map.set(gate, poi);
    } else if (name.startsWith("Boarding Area - ")) {
      const area = name.split(" - ")[1];
      if (area) map.set(area, poi);
    }
  }

  return map;
};

/**
 * Finds the associated POI ID for a flight by matching its gate and/or
 * boarding area value to the indexed POIs.
 */
const findAssociatedPoiId = (
  flight: SitumFlightInformation,
  poiByKey: Map<string, Poi>,
): number | null => {
  const keys = getPoiMatchKeys(flight);
  for (const key of keys) {
    const poi = poiByKey.get(key);
    if (poi) return poi.id;
  }
  return null;
};

/**
 * Generates an array of keys that can be used to match a flight's gate and/or
 * boarding area value to the indexed POIs.
 */
const getPoiMatchKeys = (flight: SitumFlightInformation): string[] => {
  const { gate: gateRaw, boardingArea: areaRaw } = flight.from;
  const gate = gateRaw?.trim();
  const area = areaRaw?.trim();
  const keys: string[] = [];

  if (gate) {
    const processedArea = area ?? "";
    const numericGate = Number.parseInt(gate, 10);
    if (!Number.isNaN(numericGate)) {
      keys.push(`${processedArea}${numericGate}`);
      keys.push(`${processedArea}${numericGate.toString().padStart(2, "0")}`);
    } else {
      keys.push(`${processedArea}${gate}`);
    }
  }

  if (area) keys.push(area);

  return keys;
};
